'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { SplaceWordmark } from '@/components/ui/Logo';
import { Avatar, Icon } from '@/components/ui/AppUI';
import { useAuth, isAvatarAlive, useMyAvatar, useMyLinks, linkHasUnread, useMySeekerSessions, sessionHasUnread, useSocket, startGlobalTick, stopGlobalTick } from '@splace/core';
import { Toaster } from '@/components/ui/Toaster';
import { ConfirmHost } from '@/components/ui/Confirm';
import { ConnectionStatus } from '@/components/ui/ConnectionStatus';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';

function NavItem({ href, icon, label, active, dot }: {
  href: string;
  icon: Parameters<typeof Icon>[0]['name'];
  label: string;
  active: boolean;
  dot?: boolean;
}) {
  return (
    <Link href={href} style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 12px', borderRadius: 'var(--r-sm)',
      background: active ? 'var(--bg-elev)' : 'transparent',
      color: active ? 'var(--text)' : 'var(--text-muted)',
      fontWeight: active ? 600 : 500,
      fontSize: 14, textDecoration: 'none',
      transition: 'background .12s, color .12s',
    }}>
      <Icon name={icon} size={18} />
      <span style={{ flex: 1 }}>{label}</span>
      {dot && (
        <span aria-label="New" style={{
          width: 8, height: 8, borderRadius: '50%',
          background: 'var(--accent)',
        }} />
      )}
    </Link>
  );
}

function GroupLabel({ children }: { children: string }) {
  return (
    <div style={{
      fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase',
      letterSpacing: '.08em', padding: '16px 12px 6px',
      fontFamily: 'var(--font-mono)',
    }}>
      {children}
    </div>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const pathname    = usePathname();
  const router      = useRouter();
  const user        = useAuth(s => s.user);
  const isLoading   = useAuth(s => s.isLoading);
  const { data: myAvatar } = useMyAvatar();
  const hasAvatar = !!(myAvatar && isAvatarAlive(myAvatar));
  // TanStack shares the cache with /messages — no extra fetch. The
  // count would understate (cache is first-page only), so we surface
  // only "there's something new" as a dot. Unread links float to
  // page 1 via rankedAt anyway.
  //
  // Exclude the link the user is currently viewing: a new socket
  // newMessage hits the cache before the auto-POST /read round-trip
  // returns, so for a few ms `lastMessageAt > lastReadAt` is true on
  // a chat the user is actively reading — that would blink the
  // sidebar/nav dot every incoming message.
  const currentLinkId = pathname.startsWith('/link/')
    ? pathname.slice('/link/'.length).split('/')[0]
    : null;
  const currentSessionId = pathname.startsWith('/session/')
    ? pathname.slice('/session/'.length).split('/')[0]
    : null;
  const { data: linksPages } = useMyLinks();
  const { data: seekerSessionsPages } = useMySeekerSessions();
  const messagesHasAnyUnread =
    ((linksPages?.pages.flatMap(p => p.items) ?? [])
      .filter(l => l.id !== currentLinkId)
      .some(linkHasUnread))
    || ((seekerSessionsPages?.pages.flatMap(p => p.items) ?? [])
      .filter(s => s.id !== currentSessionId)
      .some(sessionHasUnread));

  useSocket();

  // 1Hz heartbeat for the whole app: keeps every cache that carries an
  // avatar deadline self-consistent (stamps `expiredAt` locally, drops
  // expired feed entries) and drives any view that registered via
  // `useGlobalTick`. One timer for the whole tree.
  const qc = useQueryClient();
  useEffect(() => {
    startGlobalTick(qc);
    return () => stopGlobalTick();
  }, [qc]);

  if (!user && !isLoading) {
    router.replace(`/login?next=${encodeURIComponent(pathname)}`);
    return null;
  }

  const roomActive = pathname === '/avatar' || pathname.startsWith('/avatar/');
  // Chat pages take over the screen on mobile: hide the tabbar so the
  // composer + virtual keyboard fit, and let the messages list use the
  // full height. Standard pattern (WhatsApp / iMessage / Telegram).
  // The chat header already has a back button.
  const hideMobileNav = pathname.startsWith('/session/') || pathname.startsWith('/link/');

  return (
    <div data-theme="dark" style={{
      // 100dvh (not 100vh) so the layout shrinks when the iOS Safari
      // URL bar is showing — otherwise the bottom of the page (chat
      // composer, mobile nav) sits below the visible viewport.
      width: '100vw', height: '100dvh',
      background: 'var(--bg)', color: 'var(--text)',
      display: 'flex', overflow: 'hidden',
    }}>

      {/* ── Sidebar (hidden on mobile) ── */}
      <aside className="sp-sidebar">

        <Link href="/feed" style={{ padding: '8px 12px 4px', display: 'block', textDecoration: 'none' }}>
          <SplaceWordmark height={20} color="var(--accent)" textColor="var(--text)" />
        </Link>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', padding: '0 12px 20px' }}>
          avatarize
        </div>

        <GroupLabel>Real you</GroupLabel>
        <NavItem href="/feed"     icon="home" label="Feed"     active={pathname === '/feed'} />
        <NavItem href="/messages" icon="chat" label="Messages" active={pathname === '/messages' || pathname.startsWith('/messages/')} dot={messagesHasAnyUnread} />
        <NavItem href="/profile"  icon="user" label="Profile"  active={pathname === '/profile' || pathname.startsWith('/profile/')} />

        <GroupLabel>Avatar mode</GroupLabel>
        <NavItem href="/create" icon="plus" label="Create avatar" active={pathname === '/create'} />

        <button
          onClick={() => router.push(hasAvatar ? '/avatar' : '/create')}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 12px', borderRadius: 'var(--r-sm)',
            background: roomActive ? 'var(--bg-elev)' : 'transparent',
            color: roomActive ? 'var(--text)' : hasAvatar ? 'var(--text-muted)' : 'var(--text-dim)',
            fontWeight: roomActive ? 600 : 500,
            fontSize: 14, border: 'none', cursor: 'pointer', textAlign: 'left',
            width: '100%', opacity: hasAvatar ? 1 : 0.45,
            transition: 'background .12s, color .12s',
          }}
        >
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <Icon name="bolt" size={18} stroke={hasAvatar ? 'var(--accent)' : 'currentColor'} />
            {hasAvatar && (
              <span style={{
                position: 'absolute', top: -2, right: -2,
                width: 7, height: 7, borderRadius: '50%',
                background: 'var(--accent)', border: '1.5px solid var(--bg)',
              }} />
            )}
          </div>
          <span style={{ flex: 1 }}>Operations room</span>
          {hasAvatar && (
            <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent)', letterSpacing: '.04em' }}>
              LIVE
            </span>
          )}
        </button>

        <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid var(--line-soft)' }}>
          {user ? (
            <Link href="/profile" style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', borderRadius: 'var(--r-sm)',
              textDecoration: 'none', color: 'var(--text)',
              background: pathname === '/profile' ? 'var(--bg-elev)' : 'transparent',
              transition: 'background .12s',
            }}>
              <Avatar
                src={user.photoUrl}
                thumbSrc={user.photoThumbUrl}
                seed={user.username ?? user.email ?? 'me'}
                size={30}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.displayName ?? user.username ?? user.email ?? '—'}
                </div>
                {user.username && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    @{user.username}
                  </div>
                )}
              </div>
            </Link>
          ) : (
            // Store still hydrating — match the row geometry so the swap
            // when the user lands doesn't shift the sidebar layout.
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px' }}>
              <div className="skeleton" style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0 }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="skeleton" style={{ width: '70%', height: 11, marginBottom: 6 }} />
                <div className="skeleton" style={{ width: '50%', height: 10 }} />
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* ── Main ── */}
      <main className={`sp-main${hideMobileNav ? ' sp-main--no-tabbar' : ''}`}>
        {children}
      </main>

      {/* ── Mobile bottom nav ── */}
      {!hideMobileNav && (
      <nav className="sp-mobile-nav">
        {([
          { href: '/feed',     icon: 'home'  as const, label: 'Feed', dot: false },
          { href: hasAvatar ? '/avatar' : '/create', icon: hasAvatar ? 'bolt' as const : 'play' as const, label: hasAvatar ? 'Room' : 'Avatar', dot: false },
          { href: '/messages', icon: 'chat'  as const, label: 'Messages', dot: messagesHasAnyUnread },
          { href: '/profile',  icon: 'user'  as const, label: 'You', dot: false },
        ]).map(({ href, icon, label, dot }) => {
          const active = pathname === href || pathname.startsWith(href + '/');
          return (
            <Link key={href} href={href} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 3, textDecoration: 'none',
              color: active ? 'var(--text)' : 'var(--text-dim)',
              fontSize: 10, fontWeight: active ? 600 : 500,
              transition: 'color .12s',
            }}>
              <div style={{ position: 'relative' }}>
                <Icon name={icon} size={22} stroke={icon === 'bolt' && hasAvatar ? 'var(--accent)' : 'currentColor'} />
                {icon === 'bolt' && hasAvatar && (
                  <span style={{ position: 'absolute', top: -1, right: -1, width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', border: '1.5px solid var(--bg)' }} />
                )}
                {dot && (
                  <span style={{ position: 'absolute', top: -1, right: -1, width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', border: '1.5px solid var(--bg)' }} />
                )}
              </div>
              {label}
            </Link>
          );
        })}
      </nav>
      )}

      <Toaster />
      <ConfirmHost />
      <ConnectionStatus />
      <PhotoLightbox />

      <style>{`
        .sp-sidebar {
          width: 240px;
          border-right: 1px solid var(--line);
          padding: 20px 14px;
          display: flex;
          flex-direction: column;
          background: var(--bg);
          flex-shrink: 0;
          overflow: auto;
        }
        .sp-main {
          flex: 1;
          display: flex;
          overflow: hidden;
          min-width: 0;
        }
        .sp-mobile-nav { display: none; }

        @media (max-width: 768px) {
          .sp-sidebar { display: none; }
          .sp-main { padding-bottom: 64px; }
          .sp-main--no-tabbar { padding-bottom: 0; }
          .sp-mobile-nav {
            display: flex;
            position: fixed;
            bottom: 0; left: 0; right: 0;
            height: 64px;
            background: color-mix(in oklch, var(--bg) 88%, transparent);
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            border-top: 1px solid var(--line);
            z-index: 30;
            padding-bottom: env(safe-area-inset-bottom);
          }
        }
      `}</style>
    </div>
  );
}
